import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import UserStatus from "../../enums/UserStatus";
import { GET_COMPANIES } from "graphql/company";
import { useApolloClient } from "@apollo/client";
import { Select } from "@outpave/ds";
import type { SelectOption } from "@outpave/ds";
import { DEFAULT_PAGINATION_SIZE } from "./Constants";
import { useSelectSearch } from "hooks/useSelectSearch";

export interface Account {
  id: string;
  businessLegalName: string;
  numberOfUser: number;
  createdAt: string;
  lastVisit: string;
  status: string;
}

interface AccountSearchAutoCompleteProps {
  handleSelectAccount: (
    selectedAccount:
      | Account
      | null
      | undefined
      | { id: string; businessLegalName: string },
  ) => void;
  account?: Account | null | { id: string; businessLegalName: string };
  title?: string;
  /** Empty-state hint inside the field. */
  placeholder?: string;
  /** Accessible name, required when `title` is omitted. */
  "aria-label"?: string;
  /**
   * Explicit field width; the DS Select collapses to its content without one.
   * Keep `fullWidth` on with it: `width` sizes the wrapper, `fullWidth` fills it.
   */
  width?: number | string;
  additionalVariables?: Record<string, unknown>;
  errorText?: string;
  fullWidth?: boolean;
  showAllOptions?: boolean;
  required?: boolean;
  /**
   * Drives the field from a parent loading state (e.g. a drawer that is
   * still fetching its record). When true the field is disabled so it
   * stays visually in sync with the parent instead of running its own
   * async spinner.
   */
  loading?: boolean;
}

const AccountSearchAutoComplete: React.FC<AccountSearchAutoCompleteProps> = ({
  account,
  handleSelectAccount,
  title = "",
  placeholder,
  "aria-label": ariaLabel,
  width,
  additionalVariables = {},
  errorText = "",
  fullWidth = true,
  showAllOptions = false,
  required = false,
  loading = false,
}) => {
  const { t } = useTranslation();
  const client = useApolloClient();

  // Backend search for the DS Select: searchTerm plus an ACTIVE/PENDING status
  // filter, mapped into the `{ id, label }` shape the Select expects.
  const fetchPage = useCallback(
    async (query: string, after: string | null) => {
      const { data } = await client.query({
        query: GET_COMPANIES,
        variables: {
          searchTerm: query,
          status: [UserStatus.ACTIVE, UserStatus.PENDING],
          first: DEFAULT_PAGINATION_SIZE,
          after: after ?? undefined,
          ...additionalVariables,
        },
        fetchPolicy: "network-only",
      });
      const firstKey = Object.keys(data)[0];
      const connection = data[firstKey];
      const accounts: Array<{ id: string; businessLegalName: string }> =
        connection?.edges?.map(
          (e: { node: { id: string; businessLegalName: string } }) => ({
            id: e.node.id,
            businessLegalName: e.node.businessLegalName,
          }),
        ) ?? [];
      return {
        options: accounts.map((a) => ({
          id: a.id,
          label: a.businessLegalName,
        })),
        hasNextPage: connection?.pageInfo?.hasNextPage ?? false,
        endCursor: connection?.pageInfo?.endCursor ?? null,
      };
    },
    // additionalVariables and client cover the query inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, additionalVariables],
  );

  const search = useSelectSearch<SelectOption>({
    fetchPage,
    seed: useMemo(
      () =>
        account?.id
          ? { id: account.id, label: account.businessLegalName }
          : null,
      [account?.id, account?.businessLegalName],
    ),
  });

  // `showAll` is a real option, not a DS `SelectAction`: callers seed and read
  // back the literal id (see PlatformUsers), which an action can never emit.
  const options = useMemo(
    () =>
      showAllOptions
        ? [
            { id: "showAll", label: t("All Accounts") },
            ...search.options.filter((o) => o.id !== "showAll"),
          ]
        : search.options,
    [showAllOptions, search.options, t],
  );

  return (
    <Select
      size="md"
      label={title}
      placeholder={placeholder}
      aria-label={ariaLabel ?? (title ? undefined : t("Account"))}
      width={width}
      value={account?.id ?? null}
      onChange={(id) => {
        handleSelectAccount(
          id
            ? {
                id,
                businessLegalName:
                  id === "showAll"
                    ? t("All Accounts")
                    : (search.optionById(id)?.label ?? ""),
              }
            : null,
        );
        // The DS clear fires onChange only, so the list would stay filtered.
        if (!id) search.reset();
      }}
      options={options}
      loading={search.loading || loading}
      onInputChange={search.onInputChange}
      onScrollEnd={search.onScrollEnd}
      errorText={errorText}
      required={required}
      fullWidth={fullWidth}
      disabled={loading}
    />
  );
};

export default AccountSearchAutoComplete;
